<template>
  <el-drawer
    class="config-confirmation-drawer"
    v-model="visible"
    :close-on-press-escape="false"
    size="736"
    :before-close="handleClose"
  >
    <div class="title">
      {{
        showPreview
          ? t("shared.common.preview")
          : t("shared.order.confirmationConfig.title")
      }}
    </div>
    <div class="list" v-show="!showPreview">
      <!-- 语言选择 -->
      <div class="section">
        <div class="label">{{ t("shared.common.Language") }}</div>
        <div class="value">
          <div
            v-for="item in langOptions"
            :key="item.value"
            class="item"
            :class="{
              active: selectedLang === item.value
            }"
            @click="handleLangChange(item.value)"
            block
          >
            {{ t(item.label) }}
          </div>
        </div>
      </div>

      <!-- LOGO显示 -->
      <div class="section" v-if="channelShowLogo">
        <div class="label">LOGO</div>
        <div class="value">
          <div
            v-for="item in logoOptions"
            :key="item.value + 'logo'"
            class="item"
            :class="{ active: showLogo === item.value }"
            @click="handleLogoChange(item.value)"
            block
          >
            {{ t(item.label) }}
          </div>
        </div>
      </div>

      <!-- 取消政策 -->
      <div class="section">
        <div class="label">
          {{ t("shared.hotel.prepareOrder.cancellationPolicy") }}
        </div>
        <div class="value">
          <div
            v-for="item in showHideOptions"
            :key="item.value + 'cancel'"
            class="item"
            :class="{ active: showCancelPolicy === item.value }"
            @click="handleCancelPolicyChange(item.value)"
            block
          >
            {{ t(item.label) }}
          </div>
        </div>
      </div>

      <!-- 总费用 -->
      <div class="section">
        <div class="label">
          {{ t("shared.order.confirmationConfig.totalPrice") }}
        </div>
        <div class="value">
          <div
            v-for="item in totalPriceOptions"
            :key="item.value + 'total'"
            class="item"
            :class="{ active: showTotalPrice === item.value }"
            @click="handleTotalPriceChange(item.value)"
            block
          >
            {{ t(item.label) }}
          </div>
        </div>
        <div
          class="section-price-custom flex items-center mt-3"
          v-if="showTotalPrice === 'custom'"
        >
          <el-input
            class="input"
            type="number"
            :placeholder="originalPrice"
            v-model="quotedPrice"
          >
            <template #prefix>
              <div class="currency">{{ channel.currency }}</div>
            </template>
          </el-input>
        </div>
      </div>

      <!-- 费用明细 -->
      <div class="section">
        <div class="label">
          {{ t("shared.hotel.prepareOrder.priceDetail") }}
        </div>
        <div class="value">
          <div
            v-for="item in showHideOptions"
            :key="item.value + 'detail'"
            class="item"
            :class="{
              active: showPriceDetail === item.value,
              disabled: showTotalPrice === 'custom' && item.value
            }"
            @click="handlePriceDetailChange(item.value)"
            block
          >
            {{ t(item.label) }}
          </div>
        </div>
      </div>
    </div>
    <div
      class="preview h-full"
      v-if="showPreview"
      v-loading="loading"
      :element-loading-text="t('shared.order.confirmationConfig.generating')"
    >
      <img
        v-for="item in previewImageUrls"
        :src="item"
        class="w-full"
        :key="item"
      />
    </div>
    <template #footer>
      <div class="config-confirmation-footer bg-white">
        <!-- 邮箱输入 -->
        <el-input
          v-if="showEmailInput"
          :placeholder="t('shared.common.sendEmailAddress')"
          v-model="email"
        />
        <span v-else />
        <el-button
          type="primary"
          class="h-11 text-base bg-black"
          @click="handleSubmit"
          :disabled="loading"
        >
          <Loading
            v-if="loading"
            type="loading-more"
            :width="80"
            :height="80"
          />
          <span v-else>
            {{ buttonText || t("shared.order.sendConfirmation") }}
          </span>
        </el-button>
        <el-button
          class="h-11 text-base"
          @click="handlePreview"
          :disabled="loading"
          v-if="!showPreview"
        >
          {{ t("shared.common.preview") }}
        </el-button>
      </div>
    </template>
  </el-drawer>
</template>

<script setup lang="ts">
import { ElMessage } from "element-plus";
import { get } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { notifyEmail, previewConfirmation } from "@/api/biz/order";
import Loading from "@/components/Loading/index.vue";
import { useToggles } from "@/hooks/useToggles";
import { useChannelStoreHook } from "@/store/modules/channel";
import { useUserStoreHook } from "@/store/modules/user";
import { formatAmount } from "@/utils/format";
import { optimizeQiniuImage } from "@/utils/tool";
import { validateEmailMessage } from "@/utils/validate";
enum ELang {
  Simplified = "zh",
  Traditional = "zh_TW",
  English = "en",
  Japanese = "jp"
}
const DEFAULT_LANGUAGES = [
  { label: "shared.common.languageType.zh", value: ELang.Simplified },
  { label: "shared.common.languageType.zh_TW", value: ELang.Traditional },
  { label: "shared.common.languageType.en", value: ELang.English },
  { label: "shared.common.languageType.jp", value: ELang.Japanese }
];
const props = withDefaults(
  defineProps<{
    show: boolean;
    orderInfo?: any;
    defaultEmail?: string;
    showEmailInput?: boolean;
    buttonText?: string;
    saveConfig?: boolean;
  }>(),
  {
    show: false,
    showEmailInput: true,
    saveConfig: false,
    orderInfo: null
  }
);
const emit = defineEmits<{
  "update:show": [show: boolean];
}>();

const { t } = useI18n();
const showCustomPrice = useToggles("Y_CONFIRMATION_CUSTOMIZABLE_PHASE2");
const userStore = useUserStoreHook();
const channelStore = useChannelStoreHook();

const { user } = storeToRefs(userStore);

const { channel } = storeToRefs(channelStore);

const visible = ref(props.show);
const selectedLang = ref<ELang>(ELang.Simplified);
const showLogo = ref(true);
const showTotalPrice = ref<boolean | string>(true);
const showPriceDetail = ref(true);
const showCancelPolicy = ref(true);

const loading = ref(false);
const channelShowLogo = ref(true);

const email = ref(props.defaultEmail);
const quotedPrice = ref("");
const previewImageUrls = ref<string[]>([]);
const showPreview = ref(false);

const langOptions = computed(() => {
  return DEFAULT_LANGUAGES.filter(item => {
    let lang = item.value as string;
    if (lang === ELang.Traditional) {
      lang = "zh-TW";
    }
    const has = channel.value.i18n.languages.includes(lang);
    return has;
  });
});
const logoOptions = [
  { label: "shared.order.confirmationConfig.showLogo", value: true },
  { label: "shared.order.confirmationConfig.notShowLogo", value: false }
];

const showHideOptions = [
  { label: "shared.order.confirmationConfig.display", value: true },
  { label: "shared.order.confirmationConfig.hidden", value: false }
];

const showCustomPriceOption = computed(() => {
  const paymentType = get(props.orderInfo, "details.0.payment.type");
  return showCustomPrice.value && paymentType !== "payOnSite";
});
const totalPriceOptions = computed(() => {
  if (!showCustomPriceOption.value) {
    return showHideOptions;
  } else {
    return [
      ...showHideOptions,
      { label: "shared.order.confirmationConfig.customPrice", value: "custom" }
    ];
  }
});

const originalPrice = computed(() => {
  const currency = get(
    props.orderInfo,
    "_biz.charmdeerRawRender.order.calculatedPrice.aggregated.approximate.currency"
  );
  const sum = get(
    props.orderInfo,
    "_biz.charmdeerRawRender.order.calculatedPrice.aggregated.approximate.sum"
  );
  return `${t(
    "shared.order.confirmationConfig.originalPrice"
  )} ${currency} ${formatAmount(sum, "0,0.[00]")}`;
});

const handleLangChange = (lang: ELang): void => {
  selectedLang.value = lang;
};

const handleLogoChange = (value: boolean): void => {
  showLogo.value = value;
};

const handleTotalPriceChange = (value: boolean | string): void => {
  showTotalPrice.value = value;
  if (value === "custom") {
    showPriceDetail.value = false;
  }
};

const handlePriceDetailChange = (value: boolean): void => {
  showPriceDetail.value = value;
};

const handleCancelPolicyChange = (value: boolean): void => {
  showCancelPolicy.value = value;
};
const getConfirmationOptions = () => {
  return {
    language: selectedLang.value,
    displayChannelInfo: showLogo.value,
    displayPriceSummary: showTotalPrice.value,
    displayPriceBreakdown: showPriceDetail.value,
    displayCancellationPolicy: showCancelPolicy.value,
    quotedPrice:
      showTotalPrice.value === "custom" ? String(quotedPrice.value) : ""
  };
};
const checkQuotedPrice = () => {
  if (showTotalPrice.value !== "custom") {
    return true;
  }
  const sum = get(
    props.orderInfo,
    "_biz.charmdeerRawRender.order.calculatedPrice.aggregated.approximate.sum"
  );
  if (quotedPrice.value === "") {
    ElMessage.warning({
      message: t("shared.order.confirmationConfig.requireTotalPrice"),
      customClass: "message-center"
    });
    return false;
  }
  if (Number(quotedPrice.value) < sum * 0.9) {
    ElMessage.warning({
      message: t("shared.order.confirmationConfig.quotedPriceLessTips"),
      customClass: "message-center"
    });
    return false;
  }
  if (Number(quotedPrice.value) > sum * 2) {
    ElMessage.warning({
      message: t("shared.order.confirmationConfig.quotedPriceMoreTips"),
      customClass: "message-center"
    });
    return false;
  }
  return true;
};
const init = () => {
  channelShowLogo.value = get(
    channelStore.channel,
    "clients.book.options.showLogo",
    true
  );

  const { confirmationLetter = {} } = user.value?.featureToggles || {};
  selectedLang.value =
    (confirmationLetter?.language as ELang) || ELang.Simplified;
  if (channelShowLogo.value) {
    showLogo.value = confirmationLetter?.displayChannelInfo ?? true;
  } else {
    showLogo.value = false;
  }
  showTotalPrice.value = confirmationLetter?.displayPriceSummary ?? true;
  showPriceDetail.value = confirmationLetter?.displayPriceBreakdown ?? true;
  showCancelPolicy.value =
    confirmationLetter?.displayCancellationPolicy ?? true;

  email.value = props.defaultEmail || "";
  quotedPrice.value = "";
};

const doSendEmail = async () => {
  // 验证邮箱
  const { isValid, message } = validateEmailMessage(email.value);
  if (!isValid) {
    ElMessage.warning(message);
    return;
  }
  loading.value = true;

  const data = {
    confirmationOptions: getConfirmationOptions(),
    email: email.value,
    orderRef: props.orderInfo._id,
    type: "bookingSucceeded"
  };

  await notifyEmail(data);
  ElMessage.success(t("shared.order.hasSend"));
};

const doSaveConfig = async () => {
  loading.value = true;
  await userStore.updateUserToggle([
    {
      key: "confirmationLetter.language",
      value: selectedLang.value
    },
    {
      key: "confirmationLetter.displayChannelInfo",
      value: showLogo.value
    },
    {
      key: "confirmationLetter.displayPriceSummary",
      value: showTotalPrice.value
    },
    {
      key: "confirmationLetter.displayPriceBreakdown",
      value: showPriceDetail.value
    },
    {
      key: "confirmationLetter.displayCancellationPolicy",
      value: showCancelPolicy.value
    }
  ]);
  ElMessage.success(t("shared.contactInfo.SaveSuccess"));
};

const handleSubmit = async () => {
  if (loading.value || !checkQuotedPrice()) {
    return;
  }
  if (!props.saveConfig) {
    await doSendEmail();
  } else {
    await doSaveConfig();
  }
  visible.value = false;
  loading.value = false;
};
const handlePreview = async () => {
  if (!checkQuotedPrice()) {
    return;
  }
  loading.value = true;
  showPreview.value = true;
  const res = await previewConfirmation({
    orderRef: props.orderInfo._id,
    confirmationOptions: {
      confirmationOptions: getConfirmationOptions()
    }
  });
  previewImageUrls.value = optimizeQiniuImage(res.imageUrls, 10);
  loading.value = false;
};

const handleClose = done => {
  if (showPreview.value) {
    showPreview.value = false;
    done(true);
    return;
  }
  visible.value = false;
  done(false);
};
// 同步 props.show 到本地状态
watch(
  () => props.show,
  (newValue: boolean) => {
    init();
    visible.value = newValue;
  },
  { immediate: true }
);

// 同步本地状态到父组件
watch(visible, (newValue: boolean) => {
  emit("update:show", newValue);
});
</script>

<style lang="scss" scoped>
.title {
  font-family: SourceHanSerifCN, SourceHanSerifCN;
  font-weight: 600;
  font-size: 28px;
  color: #292929;
  padding: 0 20px;
}
.list {
  width: 100%;
  margin-top: 10px;
  padding: 0 20px;
}
.item {
  font-weight: 400;
  font-size: 14px;
  height: 46px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #e6e6e6;
  border-radius: 0;
  padding: 0 12px;
  color: #696969;
  width: 153px;
  cursor: pointer;
  &.active {
    border: 2px solid #000b1e;
    background: #f5f5f5;
    color: #292929;
  }
  &.disabled {
    pointer-events: none;
    opacity: 0.6;
  }
}

.section {
  margin-bottom: 36px;
  .label {
    font-size: 14px;
    color: #696969;
    margin-bottom: 14px;
  }
  .value {
    display: flex;
    gap: 12px;
  }
}

.config-confirmation-footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  box-shadow: 0px -2px 6px 0px rgba(0, 0, 0, 0.05);
  :deep(.el-input) {
    height: 44px;
    .el-input__wrapper {
      border-radius: 0;
    }
  }
  .el-button {
    width: 192px;
    height: 44px;
    border-radius: 0;
    margin-left: 12px;
  }
}
.section-price-custom {
  .el-input {
    width: 482px;
    --el-input-text-color: #0b0b0b;
  }
  :deep(.el-input__wrapper) {
    border: none;
    box-shadow: none;
    background: #f5f5f5;
    height: 46px;
    input {
      // 隐藏浏览器自带的上下箭头
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }
  .currency {
    font-weight: 500;
    font-size: 14px;
    color: #0b0b0b;
    border-right: 1px solid #eeeeee;
    padding-right: 12px;
  }
}
</style>
<style lang="scss">
.config-confirmation-drawer {
  .el-drawer__header {
    display: block;
    margin-bottom: 0;
  }
  .el-drawer__footer {
    position: relative;
    padding: 0;
  }
}
</style>
